import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { ThemeProvider } from "./ThemeContext";
import { SettingsProvider, useSettings } from "./SettingsContext";
import AppLayout from "./layouts/AppLayout";
import AdminLayout from "./layouts/AdminLayout";
import Login from "./pages/Login";
import Agents from "./pages/Agents";
import Admin from "./pages/Admin";
import CreateOrganization from "./pages/CreateOrganization";
import CreateAllowedUser from "./pages/CreateAllowedUser";
import Calendar from "./pages/Calendar";
import Settings from "./pages/Settings";
import "./styles/index.css";
import Analytics from "./pages/Analytics";

const PrivateRoute = ({ children, adminOnly = false, featureKey = null }) => {
  const { user, loading } = useAuth();
  const { isPageEnabled } = useSettings();

  if (loading) {
    return (
      <div className="app-bg min-h-screen flex items-center justify-center">
        <div className="text-on-surface-variant dark:text-on-surface-variant text-sm font-body">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/agents" />;
  }

  // Feature-flag guard: non-admin users can't reach pages hidden by settings.
  if (featureKey && !isPageEnabled(featureKey, { isAdmin: user.is_admin })) {
    return <Navigate to="/agents" />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/agents"
                element={
                  <PrivateRoute featureKey="agents">
                    <AppLayout>
                      <Agents />
                    </AppLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <PrivateRoute featureKey="calendar">
                    <AppLayout>
                      <Calendar />
                    </AppLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute adminOnly>
                    <AdminLayout>
                      <Admin />
                    </AdminLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <PrivateRoute adminOnly>
                    <AdminLayout>
                      <CreateOrganization />
                    </AdminLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <PrivateRoute featureKey="analytics">
                    <AppLayout>
                      <Analytics />
                    </AppLayout>
                  </PrivateRoute>
                }
              />
              {/* Legacy admin-only path — redirect so existing links keep working. */}
              <Route
                path="/admin/analytics"
                element={<Navigate to="/analytics" replace />}
              />
              <Route
                path="/admin/allowed-users"
                element={
                  <PrivateRoute adminOnly>
                    <AdminLayout>
                      <CreateAllowedUser />
                    </AdminLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <PrivateRoute adminOnly>
                    <AdminLayout>
                      <Settings />
                    </AdminLayout>
                  </PrivateRoute>
                }
              />
              {/* Redirect old routes */}
              <Route path="/dashboard" element={<Navigate to="/agents" />} />
              <Route path="/crm" element={<Navigate to="/agents" />} />
              <Route path="/" element={<Navigate to="/login" />} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
